import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { DateField } from "@/components/DateField";
import { PeriodPresets, RANGE_PRESETS, matchPreset } from "@/components/PeriodPresets";
import { ReportPreviewDialog } from "@/components/ReportPreviewDialog";
import { Eye } from "lucide-react";
import { PRIORITIES } from "@/components/PetitionExtraFields";
import { VillageReportOptions, VILLAGE_SECTIONS, loadVillageOpts, saveVillageOpts } from "@/components/VillageReportOptions";
import { ReportTemplatesBar } from "@/components/ReportTemplatesBar";
import { MultiVillagePicker, CheckOption } from "@/components/MultiVillagePicker";
import { Layers, Building2 } from "lucide-react";
import { FileSpreadsheet, FileText, ClipboardList, Workflow, Banknote, Mail, TrendingUp, MapPin, Tags, CalendarDays, CalendarRange } from "lucide-react";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);
const MONTHS = [
  { v: 1, l: "Ocak" }, { v: 2, l: "Şubat" }, { v: 3, l: "Mart" }, { v: 4, l: "Nisan" },
  { v: 5, l: "Mayıs" }, { v: 6, l: "Haziran" }, { v: 7, l: "Temmuz" }, { v: 8, l: "Ağustos" },
  { v: 9, l: "Eylül" }, { v: 10, l: "Ekim" }, { v: 11, l: "Kasım" }, { v: 12, l: "Aralık" },
];

const NOW = new Date();
const LAST_MONTH = new Date(NOW.getFullYear(), NOW.getMonth() - 1, 1);
const YM_PRESETS = {
  month: { year: CURRENT_YEAR, month: String(NOW.getMonth() + 1) },
  lastMonth: { year: LAST_MONTH.getFullYear(), month: String(LAST_MONTH.getMonth() + 1) },
  year: { year: CURRENT_YEAR, month: "all" },
  lastYear: { year: CURRENT_YEAR - 1, month: "all" },
};
const byKey = Object.fromEntries(RANGE_PRESETS.map((p) => [p.k, p]));
const REPORT_PRESETS = [
  byKey.today, byKey.week,
  { k: "month", l: "Bu ay" }, { k: "lastMonth", l: "Geçen ay" },
  byKey["3months"],
  { k: "year", l: "Bu yıl" }, { k: "lastYear", l: "Geçen yıl" },
];
const RANGE_ONLY = [byKey.today, byKey.week, byKey["3months"]];
const EMPTY_RANGE = { date_from: "", date_to: "" };

const REPORT_TYPES = [
  { key: "village_general", label: "Köy Genel Raporu", icon: MapPin, color: "emerald",
    desc: "Seçilen köy için yapılan tüm işlemler: çalışmalar, malzemeler, yatırımlar, altyapı, dilekçeler ve işlem geçmişi (üstteki Köy filtresinden köy seçin)" },
  { key: "multi_village", label: "Çoklu Köy Raporu", icon: Layers, color: "blue",
    desc: "Seçtiğiniz köylerin özet tablosu ve her birinin genel raporu tek dosyada" },
  { key: "district_general", label: "İlçe Genel Raporu", icon: Building2, color: "purple",
    desc: "Tüm köylerin köy köy özet tablosu ve ilçe toplamları; istenirse tüm köylerin ayrıntılı listeleri" },
  { key: "work_logs", label: "Çalışma Kayıtları", icon: ClipboardList, color: "emerald",
    desc: "Köy, kategori, miktar, tutar bazlı aylık/yıllık rapor" },
  { key: "investments", label: "Yatırımlar", icon: Banknote, color: "purple",
    desc: "Bütçe - Gerçekleşen karşılaştırma, köy/kurum bazlı" },
  { key: "infrastructure", label: "Altyapı Projeleri", icon: Workflow, color: "blue",
    desc: "İçme suyu, kanalizasyon, sulama projeleri durum raporu" },
  { key: "petitions", label: "Dilekçeler", icon: Mail, color: "orange",
    desc: "Kayıt no, nereden geldiği, talep, öncelik, ilgili kurum, yapılan işlem, sonuç, tarihler ve süre" },
  { key: "year_comparison", label: "Yıllar Arası Karşılaştırma", icon: TrendingUp, color: "rose",
    desc: "Önceki yıl / cari yıl / sonraki yıl karşılaştırmalı rapor" },
];

const MATERIAL_REPORTS = [
  { key: "material_village", label: "Köy Bazında Malzeme Maliyeti", icon: MapPin, color: "emerald",
    desc: "Her köyde kullanılan malzemeler, kullanıldığı yer, miktar, teslim tarihi ve köy toplamları" },
  { key: "material_category", label: "İş Kategorisi Bazında Malzeme Maliyeti", icon: Tags, color: "blue",
    desc: "Her iş kategorisinde kullanılan malzemeler ve kategori toplamları" },
  { key: "material_monthly", label: "Aylara Göre Malzeme Maliyeti", icon: CalendarRange, color: "purple",
    desc: "Seçilen yılın ya da tarih aralığının her ayı için toplam malzeme harcaması" },
  { key: "material_daily", label: "Günlük Malzeme Harcamaları", icon: CalendarDays, color: "orange",
    desc: "Her malzeme girişi tarih, köy ve kategoriyle tek tek" },
];

const ReportCard = ({ r, onDownload, onPreview, children }) => (
  <Card className="p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`report-card-${r.key}`}>
    <div className="flex items-start gap-4">
      <div className={`h-11 w-11 rounded-md bg-${r.color}-50 text-${r.color}-700 flex items-center justify-center flex-shrink-0`}>
        <r.icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-display font-semibold text-slate-900">{r.label}</h3>
        <p className="text-sm text-slate-500 mt-1">{r.desc}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <Button size="sm" onClick={() => onPreview(r.key)} className="bg-emerald-700 hover:bg-emerald-800" data-testid={`preview-${r.key}`}>
            <Eye className="h-3.5 w-3.5 mr-1.5" /> Önizle
          </Button>
          <Button variant="outline" size="sm" onClick={() => onDownload(r.key, "pdf")} data-testid={`export-pdf-${r.key}`}>
            <FileText className="h-3.5 w-3.5 mr-1.5 text-rose-600" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => onDownload(r.key, "excel")} data-testid={`export-excel-${r.key}`}>
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5 text-emerald-600" /> Excel
          </Button>
        </div>
        {children}
      </div>
    </div>
  </Card>
);

export default function ReportsPage() {
  const [villages, setVillages] = useState([]);
  const [insts, setInsts] = useState([]);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState("all");
  const [villageId, setVillageId] = useState("all");
  const [institutionId, setInstitutionId] = useState("all");
  const [priority, setPriority] = useState("all");
  const [source, setSource] = useState("all");
  const [sources, setSources] = useState([]);
  const [range, setRange] = useState(EMPTY_RANGE);
  const hasRange = !!(range.date_from || range.date_to);

  const pickPreset = (p) => {
    if (YM_PRESETS[p.k]) {
      setYear(YM_PRESETS[p.k].year);
      setMonth(YM_PRESETS[p.k].month);
      setRange(EMPTY_RANGE);
    } else {
      setRange(p.range());
    }
  };
  const activePreset = hasRange ? matchPreset(RANGE_ONLY, range)
    : Object.keys(YM_PRESETS).find((k) => YM_PRESETS[k].year === year && YM_PRESETS[k].month === month);

  useEffect(() => {
    api.get("/villages").then((r) => setVillages(r.data));
    api.get("/institutions").then((r) => setInsts(r.data));
    api.get("/petitions/meta").then((r) => setSources(r.data.sources));
  }, []);

  const [preview, setPreview] = useState(null);
  const [vOpts, setVOpts] = useState(loadVillageOpts);
  const updateVOpts = (o) => { setVOpts(o); saveVillageOpts(o); };
  const [multiIds, setMultiIds] = useState([]);
  const [pageBreak, setPageBreak] = useState(true);
  const [detailed, setDetailed] = useState(false);
  const [templates, setTemplates] = useState([]);
  const loadTemplates = () => api.get("/report-templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (t) => {
    updateVOpts({ usePeriod: t.use_period, sections: t.sections });
    if (t.use_period) {
      if (t.date_from || t.date_to) setRange({ date_from: t.date_from || "", date_to: t.date_to || "" });
      else { setRange(EMPTY_RANGE); if (t.year) setYear(t.year); setMonth(t.month ? String(t.month) : "all"); }
    }
    toast.success(`"${t.name}" şablonu uygulandı`);
  };
  const saveTemplate = async (name) => {
    if (!vOpts.sections.length) { toast.error("Şablon için en az bir bölüm seçin"); return false; }
    const body = { name, use_period: vOpts.usePeriod, sections: vOpts.sections,
      ...(vOpts.usePeriod ? (hasRange ? { date_from: range.date_from, date_to: range.date_to }
        : { year, month: month === "all" ? null : Number(month) }) : {}) };
    try {
      await api.post("/report-templates", body);
      toast.success("Şablon kaydedildi"); loadTemplates(); return true;
    } catch (e) { toast.error(e?.response?.data?.detail?.toString?.() || "Şablon kaydedilemedi"); return false; }
  };
  const deleteTemplate = async (t) => {
    if (!window.confirm(`"${t.name}" şablonu silinsin mi?`)) return;
    try { await api.delete(`/report-templates/${t.id}`); toast.success("Şablon silindi"); loadTemplates(); }
    catch (e) { toast.error(e?.response?.data?.detail?.toString?.() || "Şablon silinemedi"); }
  };
  const monthName = MONTHS.find((m) => String(m.v) === month)?.l;
  const periodText = hasRange
    ? `${range.date_from ? range.date_from.split("-").reverse().join(".") : "başlangıç"} – ${range.date_to ? range.date_to.split("-").reverse().join(".") : "bugün"}`
    : `${monthName ? `${monthName} ` : ""}${year}`;
  const villageParams = () => {
    const p = new URLSearchParams();
    if (vOpts.usePeriod) {
      if (hasRange) {
        if (range.date_from) p.append("date_from", range.date_from);
        if (range.date_to) p.append("date_to", range.date_to);
      } else {
        p.append("year", year);
        if (month !== "all") p.append("month", month);
      }
    }
    if (vOpts.sections.length < VILLAGE_SECTIONS.length) p.append("sections", vOpts.sections.join(","));
    return p;
  };

  const buildParams = () => {
    const params = new URLSearchParams();
    if (year) params.append("year", year);
    if (month !== "all") params.append("month", month);
    if (villageId !== "all") params.append("village_id", villageId);
    if (institutionId !== "all") params.append("institution_id", institutionId);
    if (priority !== "all") params.append("priority", priority);
    if (source !== "all") params.append("source", source);
    if (range.date_from) params.append("date_from", range.date_from);
    if (range.date_to) params.append("date_to", range.date_to);
    return params;
  };

  const villageReport = (type) => {
    if (!["village_general", "multi_village", "district_general"].includes(type)) return null;
    if (!vOpts.sections.length) { toast.error("Köy raporları için en az bir bölüm seçin"); return false; }
    if (type === "village_general") {
      if (villageId === "all") { toast.error("Köy Genel Raporu için önce Köy filtresinden bir köy seçin"); return false; }
      const p = villageParams();
      const name = villages.find((v) => v.id === villageId)?.name || "koy";
      return { preview: `/villages/${villageId}/report-preview?${p}`, file: (f) => `/villages/${villageId}/${f}?${p}`,
               name: (f) => `${name}_koy_genel_raporu.${f === "pdf" ? "pdf" : "xlsx"}` };
    }
    const p = villageParams();
    if (type === "multi_village") {
      if (!multiIds.length) { toast.error("Çoklu Köy Raporu için en az bir köy seçin"); return false; }
      p.append("village_ids", multiIds.join(","));
    } else if (detailed) p.append("detailed", "true");
    p.append("page_break", pageBreak ? "true" : "false");
    const base = type === "multi_village" ? "coklu_koy_raporu" : "ilce_genel_raporu";
    return { preview: `/district-report/preview?${p}`, file: (f) => `/district-report/${f}?${p}`,
             name: (f) => `${base}.${f === "pdf" ? "pdf" : "xlsx"}` };
  };

  const openPreview = async (type) => {
    const sp = villageReport(type);
    if (sp === false) return;
    setPreview({ type, loading: true });
    try {
      const r = await api.get(sp ? sp.preview : `/reports/${type}/preview?${buildParams()}`);
      setPreview({ type, data: r.data });
    } catch (e) {
      setPreview(null);
      toast.error(e?.response?.data?.detail?.toString?.() || "Önizleme oluşturulamadı");
    }
  };

  const download = async (type, fmt) => {
    const sp = villageReport(type);
    if (sp === false) return;
    const params = buildParams();
    const token = localStorage.getItem("khgb_token");
    try {
      const resp = await fetch(sp ? `${API}${sp.file(fmt)}` : `${API}/reports/${type}/${fmt}?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(typeof err.detail === "string" ? err.detail : "Rapor oluşturulamadı");
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = sp ? sp.name(fmt)
        : `${type}_${hasRange ? `${range.date_from || "baslangic"}_${range.date_to || "bugun"}` : `${year}${month !== "all" ? "_" + month : ""}`}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rapor indirildi");
    } catch (e) {
      toast.error(e.message || "Hata");
    }
  };

  return (
    <AppLayout
      title="Raporlar"
      subtitle="PDF ve Excel formatında rapor oluşturma"
    >
      <Card className="p-6 border border-slate-200 shadow-sm mb-6" data-testid="report-filters">
        <h3 className="font-display font-semibold text-slate-900 mb-3">Filtreler</h3>
        <div className="mb-4">
          <PeriodPresets presets={REPORT_PRESETS} active={activePreset} onPick={pickPreset} testPrefix="report" />
        </div>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <Label className="text-xs text-slate-600">Başlangıç tarihi</Label>
            <DateField value={range.date_from} onChange={(v) => setRange((r) => ({ ...r, date_from: v }))} testId="report-date-from" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Bitiş tarihi</Label>
            <DateField value={range.date_to} onChange={(v) => setRange((r) => ({ ...r, date_to: v }))} testId="report-date-to" />
          </div>
          {hasRange && (
            <Button variant="ghost" size="sm" onClick={() => setRange(EMPTY_RANGE)} data-testid="report-range-clear">Aralığı temizle</Button>
          )}
        </div>
        {hasRange && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4" data-testid="report-range-note">
            Tarih aralığı seçiliyken raporlar yıl ve ay yerine bu aralığa göre hazırlanır (Yıllar Arası Karşılaştırma hariç).
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs text-slate-600">Yıl</Label>
            <Select value={String(year)} onValueChange={(v) => { setYear(Number(v)); setRange(EMPTY_RANGE); }} disabled={hasRange}>
              <SelectTrigger data-testid="report-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Ay</Label>
            <Select value={month} onValueChange={(v) => { setMonth(v); setRange(EMPTY_RANGE); }} disabled={hasRange}>
              <SelectTrigger data-testid="report-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Yıl</SelectItem>
                {MONTHS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Köy</Label>
            <Select value={villageId} onValueChange={setVillageId}>
              <SelectTrigger data-testid="report-village-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Köyler</SelectItem>
                {villages.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Kurum</Label>
            <Select value={institutionId} onValueChange={setInstitutionId}>
              <SelectTrigger data-testid="report-institution-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Kurumlar</SelectItem>
                {insts.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Dilekçe Önceliği</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="report-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Öncelikler</SelectItem>
                {Object.entries(PRIORITIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Dilekçe Nereden Geldiği</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger data-testid="report-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <p className="md:col-span-2 self-end text-xs text-slate-500 pb-2">Öncelik ve nereden geldiği süzgeçleri yalnızca Dilekçeler raporuna uygulanır.</p>
        </div>
      </Card>

      <Card className="p-6 border border-emerald-200 bg-emerald-50/40 shadow-sm mb-6" data-testid="village-report-settings">
        <h3 className="font-display font-semibold text-slate-900">Köy, Çoklu Köy ve İlçe Raporu Seçenekleri</h3>
        <p className="text-xs text-slate-500 mt-1 mb-3">Bu seçimler Köy Genel Raporu, Çoklu Köy Raporu ve İlçe Genel Raporu için geçerlidir. Dönem, yukarıdaki filtrelerden alınır.</p>
        <ReportTemplatesBar templates={templates} onApply={applyTemplate} onSave={saveTemplate} onDelete={deleteTemplate} />
        <VillageReportOptions value={vOpts} onChange={updateVOpts} periodText={periodText} />
      </Card>

      <h3 className="font-display font-semibold text-slate-900 mb-3">Genel Raporlar</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {REPORT_TYPES.map((r) => (
          <ReportCard key={r.key} r={r} onDownload={download} onPreview={openPreview}>
            {r.key === "multi_village" && <MultiVillagePicker villages={villages} value={multiIds} onChange={setMultiIds} />}
            {r.key === "district_general" && <CheckOption checked={detailed} onChange={setDetailed} testId="district-detailed-toggle"
              label="Tüm köylerin ayrıntılı listelerini de ekle (PDF/Excel)" />}
            {(r.key === "multi_village" || (r.key === "district_general" && detailed)) && (
              <CheckOption checked={pageBreak} onChange={setPageBreak} testId={`${r.key}-page-break-toggle`}
                label="PDF'te her köy yeni sayfadan başlasın" />
            )}
          </ReportCard>
        ))}
      </div>

      <h3 className="font-display font-semibold text-slate-900 mt-10 mb-1" data-testid="material-reports-heading">Malzeme Maliyet Raporları</h3>
      <p className="text-xs text-slate-500 mb-3">Yıl, ay, tarih aralığı ve köy filtreleri bu raporlara da uygulanır. Kurum filtresi kullanılmaz.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {MATERIAL_REPORTS.map((r) => <ReportCard key={r.key} r={r} onDownload={download} onPreview={openPreview} />)}
      </div>
      <ReportPreviewDialog preview={preview} onClose={() => setPreview(null)} onDownload={download} />
    </AppLayout>
  );
}
